// 揭晓演出时序（EP-007-S3 / 主架构 §6.4 步骤 F）。
// 5 步：① 狗淡入 → ② 走到地毯 → ③ 站稳 + 气泡淡入 → ④ 礼物飘下 + 气泡填文 → ⑤ 玩家点「全部确认」→ 入空间。
// 用 CSS transform/opacity + setTimeout 推进，**零状态写入存档**（只触发 onDone → store 切 view）。
// prefers-reduced-motion: reduce → 直接跳最终态（主架构 §9）。
// 演出期间 DOM 无数字（档位名 / 进度 / 倒计时都不显示）。
// k>=2 多件：间隔 500-600ms 逐件飘下。
import { useEffect, useRef, useState } from 'react';
import type { RevealSnapshot } from '../runtime/sequencer';
import { assetIndex, type AssetId } from '../assets/assetIndex';
import { RARITY_TIER } from '../runtime/sequencer';
import { NoteBubble } from './NoteBubble';
import { RarityDots } from './RarityDots';

const ENTER_MS = 700; // 步骤①~②：狗淡入 + 走到地毯
const STAND_MS = 400; // 步骤③：站稳 + 气泡淡入
const GIFT_INTERVAL = 550; // 步骤④：礼物逐件飘下间隔（500-600ms）

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface RevealSequenceProps {
  snapshot: RevealSnapshot;
  onDone: () => void;
}

export function RevealSequence({ snapshot, onDone }: RevealSequenceProps) {
  const total = snapshot.perGift.length;
  const emptyHanded = snapshot.emptyHanded; // 🆕 空手：没有礼物，但**必须有那句话**（回归必有交代）
  const [revealed, setRevealed] = useState(0); // 已飘下礼物数
  const [showBubble, setShowBubble] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      // reduced-motion：直接跳最终态（礼物已落盘，只切 UI）
      setRevealed(total);
      setShowBubble(true);
      // ⚠️🆕 P6-POLISH-ENG-002（07 §B.7 ⚠️1）：原来是 `setShowCta(total > 0); if (total === 0) onDone();`
      //   —— 空手时**整个演出被跳过、也没给 CTA**，开了"减少动态效果"的玩家**完全看不到那句话**。
      //   total 恒 ≥ 1 时这段从没触发过，空手一开就是必现 bug。
      //   修法：空手时显示气泡 + CTA 的最终态，**等玩家点了才结束**（见下方不变量）。
      setShowCta(true);
      return;
    }

    const t0 = ENTER_MS; // 步骤③开始
    const bubbleId = setTimeout(() => setShowBubble(true), t0);
    timers.current.push(bubbleId);

    let t = t0 + STAND_MS; // 步骤④开始
    for (let i = 0; i < total; i++) {
      const id = setTimeout(() => setRevealed(i + 1), t);
      timers.current.push(id);
      t += GIFT_INTERVAL;
    }
    // ⚠️🆕 P6-POLISH-ENG-002（07 §B.7 ⚠️2）：原来这里有一段
    //   `if (total === 0) setTimeout(onDone, t0 + STAND_MS)` —— 空手时自动结束、不等玩家。
    //   已删除。**不变量：onDone 只能由玩家主动点 CTA 触发**，空手也一样，
    //   否则那句空手留言会一闪而过（这是本次新红线的核心：回归必有交代，且交代必须被看见）。
    const ctaId = setTimeout(() => setShowCta(true), t);
    timers.current.push(ctaId);

    return () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    // 仅依赖 snapshot 身份（每次揭晓都是新快照）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  return (
    <div className="reveal-stage">
      <img className="reveal-stage__dog" src={assetIndex.dog_idle} alt="" aria-hidden="true" />
      {showBubble && (
        <div className="reveal-bubble-wrap">
          <NoteBubble noteId={snapshot.noteId} />
        </div>
      )}
      <div className="reveal-gifts">
        {snapshot.perGift.slice(0, revealed).map((g) => {
          const src = assetIndex[('gift_' + g.giftId) as AssetId];
          return (
            <div key={`${g.giftId}-${g.index}`} className="reveal-gift">
              <img className="reveal-gift__img" src={src} alt="" aria-hidden="true" />
              <span className="reveal-gift__name">{g.name}</span>
              <RarityDots tier={RARITY_TIER[g.rarity]} />
            </div>
          );
        })}
      </div>
      {showCta && (
        <button type="button" className="reveal-cta" onClick={onDone}>
          {/* 🆕 空手时 CTA 文案为「摸摸它」（07 §B.7 / 06 §6 D-6）：
              把"没带礼物"转成一次**互动**，而不是"确认没有"。
              「知道了」会把注意力钉在"没有"上；「全部确认」在空手时语义不通（没有东西可确认）。 */}
          {emptyHanded ? '摸摸它' : '全部确认'}
        </button>
      )}
    </div>
  );
}
